import { useEffect, useMemo, useState } from "react";
import { Monitor, Smartphone, RefreshCw, ExternalLink, Eye } from "lucide-react";
import { withPreviewRuntime } from "./previewRuntime";

export default function PreviewPanel({ files, deployUrl, onRuntimeError }) {
  const [view, setView] = useState("desktop");
  const [nonce, setNonce] = useState(0);

  // Which of the app's pages is showing. Links between them are intercepted in
  // the frame and handed up here, because a sandboxed srcDoc has no URL to
  // navigate with — see previewRuntime.
  const [page, setPage] = useState(null);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.source !== "catalina-preview") return;

      // What the app did when it was actually RUN. This is the only evidence
      // that exists about whether the code works, and it used to die inside the
      // frame — the runtime drew a red box and told nobody who could act on it.
      if (data.error && typeof data.error.kind === "string") {
        onRuntimeError?.({
          kind: data.error.kind,
          detail: String(data.error.detail || "").slice(0, 4000),
          resource: data.error.resource ? String(data.error.resource).slice(0, 500) : null,
          page: page || "index.html",
        });
        return;
      }

      if (typeof data.path !== "string") return;
      // Only a file this project actually has. The frame is sandboxed and
      // untrusted, so what it asks for is checked rather than taken.
      if (files.some((f) => f.path === data.path)) setPage(data.path);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, onRuntimeError, page]);

  const index = files.find((f) => f.path === "index.html" || f.path.endsWith("/index.html"));
  const current = (page && files.find((f) => f.path === page)) || index;
  const html = current?.content || "";

  // A regenerated app is a different app; showing page four of the last one
  // would be showing a page that may no longer exist.
  useEffect(() => { setPage(null); }, [index?.content]);

  // The frame has no same-origin access, so the generated app needs a storage
  // shim and an error reporter injected before anything else runs — and its own
  // stylesheets and scripts resolved in, since srcDoc gives relative paths
  // nothing to resolve against.
  const srcDoc = useMemo(() => withPreviewRuntime(html, files), [html, files]);

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="h-11 shrink-0 border-b border-border flex items-center justify-between px-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Eye className="w-4 h-4" />
          <span className="font-mono text-xs uppercase tracking-wider">Live preview</span>
          {page && page !== index?.path && (
            <button data-testid="preview-home" onClick={() => setPage(null)}
              className="font-mono text-xs text-kinetic hover:text-white transition-colors"
              title="Back to index.html">
              / {page}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="preview-desktop" onClick={() => setView("desktop")}
            className={`p-1.5 rounded ${view === "desktop" ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"} transition-colors`}>
            <Monitor className="w-4 h-4" />
          </button>
          <button data-testid="preview-mobile" onClick={() => setView("mobile")}
            className={`p-1.5 rounded ${view === "mobile" ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"} transition-colors`}>
            <Smartphone className="w-4 h-4" />
          </button>
          <button data-testid="preview-refresh" onClick={() => setNonce((n) => n + 1)}
            className="p-1.5 rounded text-muted-foreground hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
          {deployUrl && (
            <a href={deployUrl} target="_blank" rel="noreferrer" data-testid="preview-open-deploy"
              className="p-1.5 rounded text-green-400 hover:text-green-300 transition-colors">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
      <div className="flex-1 relative overflow-auto flex items-center justify-center p-4 grid-bg">
        {html ? (
          <div className="absolute inset-6" style={{ filter: "drop-shadow(0 0 40px rgba(255,59,48,0.08))" }} />
        ) : null}
        {html ? (
          <div className={`relative bg-white rounded-lg overflow-hidden border border-border transition-all duration-300 ${
            view === "mobile" ? "w-[390px] h-[95%]" : "w-full h-full"
          }`}>
            <iframe key={nonce} data-testid="preview-iframe" title="preview" srcDoc={srcDoc}
              sandbox="allow-scripts allow-forms allow-popups allow-modals"
              className="w-full h-full border-0" />
          </div>
        ) : (
          <div className="text-center relative">
            <p className="font-mono text-sm text-muted-foreground">No preview yet.</p>
            <p className="text-xs text-zinc-600 mt-1">Generate an app to see it render here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
